<div class="container-fluid">
  <div class="row">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3>Installation Instructions</h3>
        <button class="btn btn-secondary" (click)="goBack()">Back to SIM</button>
      </div>

      <!-- Loading indicator -->
      @if (loading) {
        <div class="d-flex justify-content-center mt-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      }

      <!-- Error message -->
      @if (error && !loading) {
        <div class="alert alert-danger mt-3">
          {{ error }}
        </div>
      }

      <!-- Instructions content -->
      @if (!loading && !error && instructions) {
        <div class="mt-4">
          <div class="card">
            <div class="card-header bg-primary text-white">
              <h4 class="mb-0">iOS Installation Instructions</h4>
            </div>
            <div class="card-body">
              @if (instructions.ios && instructions.ios.length > 0) {
                <div>
                  <div class="row">
                    <div class="col-md-12">
                      @if (instructions.ios[0].version) {
                        <div class="alert alert-info">
                          <strong>Compatible iOS Versions:</strong> {{ instructions.ios[0].version }}
                        </div>
                      }
                    </div>
                  </div>
                  @for (iosInst of instructions.ios; track iosInst; let i = $index) {
                    <div class="border-bottom pb-3 mb-3">
                      <h5>
                        <button class="btn btn-link" (click)="toggleIosSection(i)" [attr.aria-expanded]="expandedIosSections[i]">
                          <strong>Method {{ i + 1 }}: {{
                            iosInst.installation_via_qr_code ? 'QR Code Installation' :
                            iosInst.installation_manual ? 'Manual Installation' : 'Network Setup'
                          }}</strong>
                        </button>
                      </h5>
                      @if (expandedIosSections[i]) {
                        <div>
                          <!-- QR Code Installation -->
                          @if (iosInst.installation_via_qr_code) {
                            <div class="mb-4">
                              <h6>QR Code Installation</h6>
                              <ol>
                                @for (stepKey of getStepKeys(iosInst.installation_via_qr_code.steps); track stepKey) {
                                  <li>
                                    {{ iosInst.installation_via_qr_code.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              @if (iosInst.installation_via_qr_code.qr_code_url) {
                                <div class="mt-3">
                                  <a [href]="iosInst.installation_via_qr_code.qr_code_url" target="_blank" class="btn btn-outline-primary">
                                    View QR Code
                                  </a>
                                </div>
                              }
                            </div>
                          }
                          <!-- Manual Installation -->
                          @if (iosInst.installation_manual) {
                            <div class="mb-4">
                              <h6>Manual Installation</h6>
                              <ol>
                                @for (stepKey of getStepKeys(iosInst.installation_manual.steps); track stepKey) {
                                  <li>
                                    {{ iosInst.installation_manual.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              @if (iosInst.installation_manual.smdp_address_and_activation_code) {
                                <div class="mt-3">
                                  <p><strong>SM-DP+ Address & Activation Code:</strong> {{ iosInst.installation_manual.smdp_address_and_activation_code }}</p>
                                  <p><strong>SM-DP+ Address:</strong> {{ iosInst.installation_manual.smdp_address }}</p>
                                  <p><strong>Activation Code:</strong> {{ iosInst.installation_manual.activation_code }}</p>
                                </div>
                              }
                            </div>
                          }
                          <!-- Network Setup -->
                          @if (iosInst.network_setup) {
                            <div>
                              <h6>Network Setup</h6>
                              <ol>
                                @for (stepKey of getStepKeys(iosInst.network_setup.steps); track stepKey) {
                                  <li>
                                    {{ iosInst.network_setup.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              <div class="mt-3">
                                <p><strong>APN Type:</strong> {{ iosInst.network_setup.apn_type }}</p>
                                @if (iosInst.network_setup.apn_value) {
                                  <p><strong>APN Value:</strong> {{ iosInst.network_setup.apn_value }}</p>
                                }
                                <p><strong>Roaming:</strong> {{ iosInst.network_setup.is_roaming ? 'Enabled' : 'Disabled' }}</p>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              } @else {
                <p class="text-muted">No iOS installation instructions available.</p>
              }
            </div>
          </div>
          <div class="card mt-4">
            <div class="card-header bg-success text-white">
              <h4 class="mb-0">Android Installation Instructions</h4>
            </div>
            <div class="card-body">
              @if (instructions.android && instructions.android.length > 0) {
                <div>
                  @for (androidInst of instructions.android; track androidInst; let i = $index) {
                    <div class="border-bottom pb-3 mb-3">
                      <h5>
                        <button class="btn btn-link" (click)="toggleAndroidSection(i)" [attr.aria-expanded]="expandedAndroidSections[i]">
                          <strong>Method {{ i + 1 }}: {{
                            androidInst.installation_via_qr_code ? 'QR Code Installation' :
                            androidInst.installation_manual ? 'Manual Installation' : 'Network Setup'
                          }}</strong>
                        </button>
                      </h5>
                      @if (expandedAndroidSections[i]) {
                        <div>
                          <!-- QR Code Installation -->
                          @if (androidInst.installation_via_qr_code) {
                            <div class="mb-4">
                              <h6>QR Code Installation</h6>
                              <ol>
                                @for (stepKey of getStepKeys(androidInst.installation_via_qr_code.steps); track stepKey) {
                                  <li>
                                    {{ androidInst.installation_via_qr_code.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              @if (androidInst.installation_via_qr_code.qr_code_url) {
                                <div class="mt-3">
                                  <a [href]="androidInst.installation_via_qr_code.qr_code_url" target="_blank" class="btn btn-outline-success">
                                    View QR Code
                                  </a>
                                </div>
                              }
                            </div>
                          }
                          <!-- Manual Installation -->
                          @if (androidInst.installation_manual) {
                            <div class="mb-4">
                              <h6>Manual Installation</h6>
                              <ol>
                                @for (stepKey of getStepKeys(androidInst.installation_manual.steps); track stepKey) {
                                  <li>
                                    {{ androidInst.installation_manual.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              @if (androidInst.installation_manual.smdp_address_and_activation_code) {
                                <div class="mt-3">
                                  <p><strong>SM-DP+ Address & Activation Code:</strong> {{ androidInst.installation_manual.smdp_address_and_activation_code }}</p>
                                </div>
                              }
                            </div>
                          }
                          <!-- Network Setup -->
                          @if (androidInst.network_setup) {
                            <div>
                              <h6>Network Setup</h6>
                              <ol>
                                @for (stepKey of getStepKeys(androidInst.network_setup.steps); track stepKey) {
                                  <li>
                                    {{ androidInst.network_setup.steps[stepKey] }}
                                  </li>
                                }
                              </ol>
                              <div class="mt-3">
                                <p><strong>APN Type:</strong> {{ androidInst.network_setup.apn_type }}</p>
                                @if (androidInst.network_setup.apn_value) {
                                  <p><strong>APN Value:</strong> {{ androidInst.network_setup.apn_value }}</p>
                                }
                                <p><strong>Roaming:</strong> {{ androidInst.network_setup.is_roaming ? 'Enabled' : 'Disabled' }}</p>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              } @else {
                <p class="text-muted">No Android installation instructions available.</p>
              }
            </div>
          </div>
        </div>
      }

      <!-- No instructions message -->
      @if (!loading && !error && !instructions) {
        <div class="alert alert-info mt-3">
          No instructions available for this SIM.
        </div>
      }
    </div>
  </div>
</div>